import { FC, Dispatch, SetStateAction, useState, useMemo } from 'react'
import { AttorneyData } from '../../../dtos/applicationData'
import { AboutAttorneyFormContainer, OptionButtonsContainer } from '../defaultComponents'
import { LeftBar } from '../../basic/LeftBar'
import { FormInputErrorMessage } from './styles'
import StandardFormatInput from '../../basic/StandardFormatInput'
import Button from '../../basic/Button'
import OptionButton from '../../basic/OptionButton'
import StepBullet from '../../basic/StepBullet'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { object, string, bool } from 'yup'
import { validateDateField } from 'src/utils/formDataManipulation'

export type LicensingSectionData = AttorneyData['license']

interface AttorneyFormLicensingSectionProps {
  licensingSectionData: LicensingSectionData;
  setLicensingSectionData: Dispatch<SetStateAction<LicensingSectionData>>;
  handlePageResize: () => void;
  formInitialValidation: boolean;
  onSaveDetails: () => Promise<void>;
  isOpen: boolean;
  handleOpen: () => void;
  showErrors?: boolean;
}

const AttorneyFormLicensingSection: FC<AttorneyFormLicensingSectionProps> = ({
  licensingSectionData,
  setLicensingSectionData,
  handlePageResize,
  formInitialValidation,
  onSaveDetails,
  isOpen,
  handleOpen,
  showErrors
}) => {
  const [isAccordion, setIsAccordion] = useState<boolean>(formInitialValidation)
  const [disabledSaveDetailsButton, setDisabledSaveDetailsButton] = useState(false)

  const handleChange = (name: string, value: any) => {
    setLicensingSectionData(prev => ({ ...prev, [name]: value }))
  }

  const formContainerClasses = `form-container ${
    isAccordion ? 
    (isOpen ? 'accordion-open' : 'accordion-closed') :
    ''
  }`

  const handleSaveDetails = async () => {
    setDisabledSaveDetailsButton(true)
    await onSaveDetails()
    setIsAccordion(true)
    handlePageResize()
    setDisabledSaveDetailsButton(false)
  }
  
  const handleToggleAccordion = () => {
    handleOpen()
    handlePageResize()
  }

  const validationSchema = object({
    licenseDate: string().validDate('licensingSection').required('licensingSection'),
    isLicensedInOtherState: bool().required('licensingSection')
  })

  const isFormValid = useMemo(() => {
    try {
      validationSchema.validateSync(licensingSectionData)
      return true
    } catch(e) {
      return false
    }
  }, [licensingSectionData, validationSchema])

  const isInputIncorrect = () => {
    return showErrors && !isFormValid
  }

  return (
    <AboutAttorneyFormContainer>
      <LeftBar />
      <div className={formContainerClasses}>
        <div className='form-title'>
          <StepBullet isComplete>1</StepBullet>
          <h6>LICENSING</h6>
          <button onClick={handleToggleAccordion}>
            {isOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdKeyboardArrowDown />
            )}
          </button>
        </div>
        <div className='form-content'>
          <StandardFormatInput
            label='Date licensed in Texas'
            maskStyle='date'
            value={licensingSectionData?.licenseDate ?? ''}
            leftIcon="calendar"
            onValueChange={({ formattedValue }) => handleChange('licenseDate', formattedValue)}
            isIncorrect={isInputIncorrect() && !!validateDateField(licensingSectionData?.licenseDate)}
            errorMessage={isInputIncorrect() ? validateDateField(licensingSectionData?.licenseDate) : ''}
          />
          <OptionButtonsContainer>
            <span>Is this attorney licensed in another state?</span>
            <div>
              <OptionButton
                checked={licensingSectionData?.isLicensedInOtherState === true}
                onChange={() => handleChange('isLicensedInOtherState', true)}
                isIncorrect={isInputIncorrect() && licensingSectionData?.isLicensedInOtherState === undefined}
              >
                Yes
              </OptionButton>
              <OptionButton
                checked={licensingSectionData?.isLicensedInOtherState === false}
                onChange={() => handleChange('isLicensedInOtherState', false)}
                isIncorrect={isInputIncorrect() && licensingSectionData?.isLicensedInOtherState === undefined}
              >
                No
              </OptionButton>
            </div>
            {isInputIncorrect() && licensingSectionData?.isLicensedInOtherState === undefined ? (
              <FormInputErrorMessage>
                This is a required field
              </FormInputErrorMessage>
            ) : null}
          </OptionButtonsContainer>
          <Button 
            onClick={() => handleSaveDetails()}
            disabled={disabledSaveDetailsButton ? true : !isFormValid}
          >
            {disabledSaveDetailsButton ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </AboutAttorneyFormContainer>
  )
}

export default AttorneyFormLicensingSection